// iter217 — Master Profitability View (Phase 3)
// Admin/Owner only. Sales − COGS − Direct Opex − Overheads − Interest = Net Profit.
// Monthly trend + Star vs Leak product ranking.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, AlertTriangle, Lock, RefreshCw, Calendar,
  Star, Trophy, AlertCircle, DollarSign,
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, BarChart,
} from 'recharts';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function Profitability() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const [mr, rr] = await Promise.all([
        axios.get(`${API}/profitability/master`, {
          params: { months },
          withCredentials: true, timeout: 30000,
        }),
        axios.get(`${API}/profitability/product-rank`, {
          params: { days: months * 30 },
          withCredentials: true, timeout: 30000,
        }),
      ]);
      setData(mr.data);
      setRanking(rr.data);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Failed to load';
      setError(detail);
      toast.error(detail);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [months]);

  const trendChartData = useMemo(() => {
    if (!data?.rows) return [];
    return data.rows.map(r => ({
      month: r.month.slice(5), // MM
      sales: r.sales,
      cogs: r.cogs,
      net_profit: r.net_profit,
      net_pct: r.net_profit_pct,
    }));
  }, [data]);

  if (!isAdmin) return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 flex items-center gap-3" data-testid="profitability-no-access">
      <Lock size={20} className="text-amber-400" />
      <div>
        <div className="text-zinc-50 font-semibold">Admin-only area</div>
        <div className="text-zinc-500 text-sm">Master profitability + product margins are admin/owner only.</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="profitability-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Master Profitability</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Sales − COGS − Opex − Overheads − Interest = Net Profit (admin only)</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-1 flex">
            {[3, 6, 12].map(m => (
              <button key={m} onClick={() => setMonths(m)} data-testid={`range-${m}m`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${months === m ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}>
                {m}M
              </button>
            ))}
          </div>
          <button onClick={fetchData} className="p-2 text-zinc-400 hover:text-amber-300" title="Refresh">
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center text-zinc-500 text-sm animate-pulse">
          Crunching {months} months of P&amp;L…
        </div>
      ) : error ? (
        <div className="bg-red-900/15 border border-red-700/50 rounded-xl p-4" data-testid="profitability-error">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="text-red-300 mt-0.5" />
            <div className="flex-1">
              <div className="text-red-200 text-sm font-semibold">Could not load profitability</div>
              <div className="text-red-100/80 text-[11px] mt-1 break-words">{error}</div>
              <button onClick={fetchData} className="mt-2 text-[11px] bg-red-700/40 hover:bg-red-700/60 border border-red-600/60 text-red-100 px-3 py-1 rounded-md">
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Window totals KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI label={`Sales (${months}M)`} value={fmt(data?.totals?.sales)} icon={DollarSign} tone="amber" testid="kpi-sales" />
            <KPI label="COGS" value={fmt(data?.totals?.cogs)} icon={TrendingDown} tone="red" testid="kpi-cogs" />
            <KPI label="Overheads" value={fmt(data?.totals?.overheads)} icon={TrendingDown} tone="blue" testid="kpi-overheads" />
            <KPI label="Interest" value={fmt(data?.totals?.interest)} icon={TrendingDown} tone="rose" testid="kpi-interest" />
            <KPI
              label="Net Profit"
              value={fmt(data?.totals?.net_profit)}
              icon={data?.totals?.net_profit >= 0 ? TrendingUp : TrendingDown}
              tone={data?.totals?.net_profit >= 0 ? 'emerald' : 'red'}
              sub={`${data?.totals?.net_profit_pct || 0}% margin`}
              testid="kpi-net-profit"
            />
          </div>

          {/* Trend chart */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-4 flex items-center gap-2">
              <Calendar size={14} className="text-amber-400" /> Monthly Trend — Sales vs COGS + Net Profit
            </h3>
            <ResponsiveContainer width="100%" height={290}>
              <ComposedChart data={trendChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="month" tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false}
                  tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: '#71717A', fontSize: 11 }}
                  axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: '8px', color: '#FAFAFA', fontSize: '12px' }}
                  formatter={(v, name) => name === 'net_pct' ? [`${v}%`, 'Net Margin %'] : [fmt(v), name === 'sales' ? 'Sales' : name === 'cogs' ? 'COGS' : 'Net Profit']}
                />
                <Legend wrapperStyle={{ fontSize: '11px', color: '#A1A1AA' }} />
                <Bar yAxisId="left" dataKey="sales" name="Sales" fill="#F59E0B" />
                <Bar yAxisId="left" dataKey="cogs" name="COGS" fill="#EF4444" />
                <Line yAxisId="left" dataKey="net_profit" name="Net Profit" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                <Line yAxisId="right" dataKey="net_pct" name="Net Margin %" stroke="#A78BFA" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly P&L table */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-zinc-800 text-zinc-400 text-xs font-medium">
              Month-by-Month P&amp;L
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead className="bg-zinc-950/50">
                  <tr className="border-b border-zinc-800">
                    {['Month', 'Sales', 'COGS', 'Gross', 'GM %', 'Overheads', 'Opex', 'Interest', 'Net Profit', 'Net %', 'Kg Sold'].map(h => (
                      <th key={h} className="text-left text-zinc-500 text-xs font-medium px-3 py-2.5">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="pnl-table-body">
                  {(data?.rows || []).map(r => (
                    <tr key={r.month} className="border-b border-zinc-800/40 hover:bg-zinc-800/30" data-testid={`pnl-row-${r.month}`}>
                      <td className="px-3 py-2.5 font-mono text-zinc-200 text-xs">{r.month}</td>
                      <td className="px-3 py-2.5 font-mono text-amber-300 text-xs">{fmt(r.sales)}</td>
                      <td className="px-3 py-2.5 font-mono text-red-300 text-xs">{fmt(r.cogs)}</td>
                      <td className="px-3 py-2.5 font-mono text-zinc-300 text-xs">{fmt(r.gross_margin)}</td>
                      <td className="px-3 py-2.5 font-mono text-zinc-400 text-xs">{r.gross_margin_pct}%</td>
                      <td className="px-3 py-2.5 font-mono text-blue-300 text-xs">{fmt(r.overheads)}</td>
                      <td className="px-3 py-2.5 font-mono text-zinc-400 text-xs">{fmt(r.direct_opex)}</td>
                      <td className="px-3 py-2.5 font-mono text-rose-300 text-xs">{fmt(r.interest)}</td>
                      <td className={`px-3 py-2.5 font-mono text-xs font-semibold ${r.net_profit >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>
                        {fmt(r.net_profit)}
                      </td>
                      <td className={`px-3 py-2.5 font-mono text-xs ${r.net_profit_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {r.net_profit_pct}%
                      </td>
                      <td className="px-3 py-2.5 font-mono text-zinc-500 text-xs">{fmtNum(r.units_sold_kg)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Star vs Leak products */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-zinc-900 border border-emerald-800/40 rounded-xl p-5">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-3 flex items-center gap-2">
                <Trophy size={14} className="text-emerald-400" /> Star Products
                <span className="text-[10px] text-zinc-500">(by margin %)</span>
              </h3>
              <ProductList rows={ranking?.stars || []} positive />
            </div>
            <div className="bg-zinc-900 border border-red-800/40 rounded-xl p-5">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-3 flex items-center gap-2">
                <AlertCircle size={14} className="text-red-400" /> Margin Leaks
                <span className="text-[10px] text-zinc-500">(lowest margin)</span>
              </h3>
              <ProductList rows={ranking?.leaks || []} positive={false} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const TONE_MAP = {
  amber: 'border-amber-800/40 text-amber-300',
  red: 'border-red-800/40 text-red-300',
  rose: 'border-rose-800/40 text-rose-300',
  blue: 'border-blue-800/40 text-blue-300',
  emerald: 'border-emerald-800/40 text-emerald-300',
};

function KPI({ label, value, icon: Icon, tone = 'amber', sub, testid }) {
  return (
    <div className={`bg-zinc-900 border rounded-xl p-4 ${TONE_MAP[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-zinc-400 text-xs">{label}</span>
        {Icon && <Icon size={13} className="opacity-70" />}
      </div>
      <div className="font-mono font-bold text-xl">{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ProductList({ rows, positive }) {
  if (!rows || rows.length === 0) {
    return <div className="text-zinc-500 text-xs py-6 text-center">Not enough data yet.</div>;
  }
  return (
    <div className="space-y-2" data-testid={positive ? 'star-products' : 'leak-products'}>
      {rows.map((r, i) => (
        <div key={r.product_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-950 border border-zinc-800">
          <div className={`font-mono text-xs w-6 flex-shrink-0 ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
            {positive ? '#' + (i + 1) : '#' + (i + 1)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-zinc-200 text-sm font-medium truncate" title={r.product_name}>{r.product_name || r.product_id}</div>
            <div className="text-zinc-500 text-[10px] mt-0.5">
              {fmtNum(r.qty_kg)} kg · revenue {fmt(r.revenue)} · COGS {fmt(r.cogs)}
            </div>
          </div>
          <div className="text-right">
            <div className={`font-mono text-sm font-semibold ${r.margin_pct >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>
              {r.margin_pct}%
            </div>
            <div className="text-[10px] text-zinc-500">{fmt(r.margin)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
